import {isFunction, noop} from 'lodash';
import React, {FC, useEffect, useRef} from 'react';

import {globalWindow} from '../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {getWindow} from '../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {FrontConfig} from '../../../../libs/shared/core/src/helpers/bundler/bundlerDefineConfig';
import {useEventHandler} from '../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {ExtendedError} from '../../../../libs/shared/core/src/helpers/types/customErrors';
import {CancelError} from '../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {useSafeArea} from '../../../../libs/shared/ui/src/components/safeArea/safeAreaContext';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {getHelpChatConfig} from '../../../store/data/dataSelectors';
import {FrontChatCommandsEnum, FrontChatSdk, FrontChatTreeData} from './types';

/*
 * Types.
 */

interface FrontChatProps {
  chatId: string;
  bootCommand?: FrontChatCommandsEnum;
  isDefaultLauncherEnabled?: boolean;
  isChatWindowVisible?: boolean;
  userEmail?: string;
  userHash?: string;
  treeData?: FrontChatTreeData;
  bundleSrc?: string;
  onStatusChange?: (isInitialized: boolean) => void;
  onClose?: () => void;
}

/*
 * Component.
 */

const launcherSize = 64;
const launcherMargin = 16;

export const FrontChat: FC<FrontChatProps> = (props) => {
  const {
    chatId,
    bootCommand = FrontChatCommandsEnum.INIT,
    isDefaultLauncherEnabled = true,
    isChatWindowVisible = false,
    userEmail,
    userHash,
    treeData,
    onStatusChange,
    onClose,
  } = props;

  const chatContainerRef = useRef<HTMLDivElement>(null);

  const helpChatConfig = useAppSelector(getHelpChatConfig);
  const bundleSrc =
    props.bundleSrc ?? helpChatConfig?.bundleSrc ?? 'https://chat-assets.frontapp.com/v1/chat.bundle.js';

  const namespace = computeNamespace(bundleSrc);

  const frontChatSdkRef = useRef<FrontChatSdk | undefined>(undefined);

  const {backgroundSafeArea, registerForegroundSafeAreaWithInsets} = useSafeArea();
  const onBackgroundSafeAreaSync = useEventHandler(() => {
    frontChatSdkRef.current?.(FrontChatCommandsEnum.SET_PAGE_INSET, backgroundSafeArea);
  });

  // Update the chat widget location when the background safe area changes.
  useEffect(() => {
    onBackgroundSafeAreaSync();
  }, [backgroundSafeArea, onBackgroundSafeAreaSync]);

  // Register a foreground safe area for the chat widget.
  useEffect(
    () =>
      registerForegroundSafeAreaWithInsets({
        right: launcherSize + launcherMargin * 2,
        bottom: launcherSize + launcherMargin * 2,
        shouldAddBackgroundSafeArea: true,
      }),
    [registerForegroundSafeAreaWithInsets],
  );

  // This effect allows the component that is rendering FrontChat to update its own visibility status
  // if the chat window is forcibly closed.
  useEffect(() => {
    if (!onClose) {
      return noop;
    }

    const window = getWindow(chatContainerRef.current);
    window?.addEventListener('beforeunload', onClose);

    return () => {
      window?.removeEventListener('beforeunload', onClose);
    };
  }, [onClose]);

  // This effect will boot FrontChat when first mounting, and then run the initial SDK command.
  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (!chatContainer) {
      return noop;
    }

    // If this component unmounts before the chat SDK boots up, we should no longer attempt to
    // fire the initial SDK command.
    let isCancelled = false;

    const frontChatSdkPromise = boot(chatContainer, bundleSrc).then((frontChatSdk: FrontChatSdk) => {
      if (isCancelled) {
        throw new CancelError();
      }

      return new Promise<FrontChatSdk>((resolve) => {
        frontChatSdk(bootCommand, {
          chatId,
          namespace,
          useDefaultLauncher: isDefaultLauncherEnabled,
          shouldShowWindowOnLaunch: isChatWindowVisible,
          userHash,
          email: userEmail,
          treeData,
          onInitCompleted: () => {
            resolve(frontChatSdk);
          },
        });
      });
    });

    // Inform the caller of this component that the FrontChat SDK has booted and initialized.
    frontChatSdkPromise.then((frontChatSdk) => {
      if (isCancelled) {
        return;
      }

      frontChatSdkRef.current = frontChatSdk;
      onStatusChange?.(true);
      onBackgroundSafeAreaSync();
    });

    return () => {
      onStatusChange?.(false);
      frontChatSdkRef.current = undefined;
      isCancelled = true;

      frontChatSdkPromise.then((frontChatSdk: FrontChatSdk) => {
        frontChatSdk(FrontChatCommandsEnum.SHUTDOWN);
      });
    };
  }, [
    bundleSrc,
    chatId,
    bootCommand,
    isDefaultLauncherEnabled,
    isChatWindowVisible,
    namespace,
    onStatusChange,
    onBackgroundSafeAreaSync,
    userEmail,
    userHash,
    treeData,
  ]);

  // The `shouldShowWindowOnLaunch` option can be provided to the `init` command to open the chat window on initialize.
  // For subsequent changes in the `isChatWindowVisible` prop, we need to call the `show` and `hide` SDK commands to
  // change the visibility.
  useEffect(() => {
    const frontChatSdk = frontChatSdkRef.current;
    if (!frontChatSdk) {
      return;
    }

    if (isChatWindowVisible) {
      frontChatSdk(FrontChatCommandsEnum.SHOW);
    } else {
      frontChatSdk(FrontChatCommandsEnum.HIDE);
    }
  }, [isChatWindowVisible]);

  return <div ref={chatContainerRef} />;
};

/*
 * Helpers.
 */

async function boot(element: HTMLElement, bundleSrc: string): Promise<FrontChatSdk> {
  const scriptTag = document.createElement('script');
  scriptTag.setAttribute('type', 'text/javascript');
  scriptTag.setAttribute('src', bundleSrc);

  const parentWindow = getWindow(element);
  if (!parentWindow) {
    throw new Error('Window for element not found');
  }

  // Do not boot multiple times for a given window.
  if ('FrontChat' in parentWindow && isFunction(parentWindow.FrontChat)) {
    return parentWindow.FrontChat;
  }

  const loadScriptPromise = new Promise<FrontChatSdk>((resolve, reject) => {
    // The boot promise should only resolve once the app.bundle.js script has fully loaded.
    // Once loaded, the FrontChat SDK should be attached to the parentWindow.
    scriptTag.addEventListener('load', () => {
      if ('FrontChat' in parentWindow && isFunction(parentWindow.FrontChat)) {
        resolve(parentWindow.FrontChat);
      } else {
        reject(new Error('Could not setup parentWindow.FrontChat'));
      }
    });

    scriptTag.addEventListener('error', (event) => {
      reject(new ExtendedError('Could not load chat bundle', event.error));
    });
  });

  // The `load` event defined above will fire once the chat.bundle.js script is added to the parentWindow document.
  parentWindow.document.head.appendChild(scriptTag);

  return loadScriptPromise;
}

function computeNamespace(bundleSrc: string): string | undefined {
  // This is a production bundle, no need for namespace.
  if (!bundleSrc.includes('kube_staging')) {
    return undefined;
  }

  // When deployed on k8s, the namespace is available in the subdomain.
  const {hostname} = globalWindow.location;
  const stagingHostnameMatch = hostname.match(/^app\.(.+)\.myfront\.dev$/);
  if (stagingHostnameMatch) {
    const stagingNamespace = stagingHostnameMatch[1];
    return stagingNamespace;
  }

  // When running locally, STAGING_NAMESPACE should be defined.
  return FrontConfig.STAGING_NAMESPACE;
}
